import React from "react";
import { Breadcrumb } from "../../../../atoms/breadcrumb";
import { useTableHooks } from "../../../../atoms/table/useTableHooks";
import { hasPermission, pascalToSnakeCase } from "../../../../../utils";
import {
    useLicenseRequisitionQuery,
    useUpdateBufferLicenseMutation,
} from "../../../../../store/license/licenseSlice";
import { Table } from "../../../../atoms/table";
import { Input } from "../../../../atoms/input";
import {
    payloadType,
    ResponseType,
    TableDataType,
} from "../../../../../types";
import {
    PAGE_NAME,
    PERMISSIONS,
    ROUTE,
    SOMETHING_WENT_WRONG,
} from "../../../../../constants/constants";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Modal } from "../../../../molecules/modal";
import { Divider } from "../../../../atoms/divider";
import { Loader } from "../../../../atoms/loader";

const formatColumnName = (name: string) => {
    switch (name) {
        case "totalLicenses":
            return "total";
        case "updatedAtTime":
            return "updatedAt";
        default:
            return name;
    }
};
const LicenseRequisition = () => {
    const pageClassName = "p-licenseRequisition";
    const navigate = useNavigate();
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const [payload, setPayload] = React.useState<payloadType>({
        id: {
            value: "",
            error: "",
            isError: false,
        },
        saas_tool: {
            value: "",
            error: "",
            isError: false,
        },
        buffer: {
            value: 0,
            error: "",
            isError: false,
        },
        additional: {
            value: 0,
            error: "",
            isError: false,
        },
    });

    const tableHeader = [
        {
            title: "Serial",
            dataIndex: "id",
            isSortable: true,
        },
        {
            title: "Saas Name",
            dataIndex: "name",
            isSortable: true,
        },
        {
            title: "Total",
            dataIndex: "total",
            isSortable: true,
        },
        {
            title: "Active",
            dataIndex: "active",
            isSortable: true,
        },
        {
            title: "Invited",
            dataIndex: "invited",
            isSortable: true,
        },
        {
            title: "In Queue",
            dataIndex: "inQueue",
            isSortable: true,
            permission: hasPermission(PERMISSIONS.LICENSE_EDIT.title),
        },
        {
            title: "Predicted",
            dataIndex: "predicted",
            isSortable: true,
        },
        {
            title: "Additional",
            dataIndex: "additional",
            isSortable: true,
        },
        {
            title: "Buffer",
            dataIndex: "buffer",
            isSortable: true,
        },
        {
            title: "Requisition",
            dataIndex: "requisition",
            isSortable: true,
        },
        {
            title: "Action",
            dataIndex: "actionEdit",
            isSortable: false,
        }
    ];

    const { search, query, sortHandler, searchHandler } = useTableHooks();

    const { data, isLoading } = useLicenseRequisitionQuery({
        search: query.search,
        limit: query.limit,
        current_page: query.current,
        sort_by: pascalToSnakeCase(formatColumnName(query.sortBy)),
        sort_direction: query.sortDirection,
    });
    const [updateApi, { isLoading: updateLoading }] = useUpdateBufferLicenseMutation();
    const editHandler = (id: string | number) => {
        const license = data?.result?.data.find(
            (item: TableDataType) => item.id === id
        );
        setIsModalOpen(true);
        setPayload({
            id: {
                value: id,
                error: "",
                isError: false,
            },
            saas_tool: {
                value: license?.name,
                error: "",
                isError: false,
            },
            buffer: {
                value: license?.buffer,
                error: "",
                isError: false,
            },
            additional: {
                value: license?.additional,
                error: "",
                isError: false,
            },
        });
    };

    const redirectHandler = (name: string) => {
        switch (name) {
            case "Okta":
                navigate(ROUTE.OKTA_INVITED_USER_LIST);
                break;
            case "GitLab":
                navigate(ROUTE.GITLAB_INVITED_USER_LIST);
                break;
            case "Confluence":
                navigate(ROUTE.CONFLUENCE_INVITED_USER_LIST);
                break;
            case "Atlassian":
                navigate(ROUTE.ATLASSIAN_INVITED_USER_LIST);
                break;
            case "Miro":
                navigate(ROUTE.MIRO_INVITED_USER_LIST);
                break;
            case "Youtrack":
                navigate(ROUTE.YOUTRACK_INVITED_USER_LIST);
                break;
            case "Box":
                navigate(ROUTE.BOX_INVITED_USER_LIST);
                break;
            case "GCP":
                navigate(ROUTE.GCP_INVITED_USER_LIST);
                break;
            case "SOPHOS MDR":
                navigate(ROUTE.SOPHOS_INVITED_USER_LIST);
                break;
            default:
                toast.warn(SOMETHING_WENT_WRONG);
                break;
        }
    };

    const submitHandler = async (e: React.FormEvent) => {
        e.preventDefault();
        const formData = new FormData();
        formData.append("buffer", payload.buffer.value.toString());
        formData.append("additional", payload.additional.value.toString());
        formData.append("id", payload.id.value as string);
        const response = await updateApi(formData) as ResponseType;
        if (response?.data?.success) {
            toast.success(response?.data?.message);
            setIsModalOpen(false);
        } else {
            if (response?.error?.data?.errors) {
                Object.keys(response?.error?.data?.errors).forEach((key) => {
                    setPayload((prevState) => ({
                        ...prevState,
                        [key]: {
                            ...prevState[key],
                            error: response?.error?.data?.errors[key][0],
                            isError: true,
                        },
                    }));
                    //   }
                });
            } else {
                toast.error(response?.error?.data?.message);
            }
        };
    }


    return (
        <div className={pageClassName}>
            <Breadcrumb />
            <p className={`${pageClassName}__title`}>
                {PAGE_NAME.LICENSE_REQUISITION}
            </p>

            <div className={`${pageClassName}__container`}>
                <div className={`${pageClassName}__header`}>
                    <div className={`${pageClassName}__search`}>
                        <Input
                            placeholder="Search"
                            onChange={searchHandler}
                            value={search}
                        />
                    </div>
                </div>
                <div className={`${pageClassName}__table`}>
                    <Table
                        header={tableHeader}
                        data={data?.result?.data ?? []}
                        loading={isLoading}
                        firstLoad={isLoading}
                        sortHandler={sortHandler}
                        sortBy={query.sortBy}
                        minWidth="1440"
                        startIndex={data?.result?.from}
                        editHandler={editHandler}
                        deleteHandler={redirectHandler}
                        viewURL={ROUTE.LICENSE_MANAGEMENT}
                        cursorPointer
                    />
                </div>
            </div>
            <Modal
                header={`Update Additional & Buffer License for ${payload.saas_tool.value}`}
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                width="small"
            >
                <Loader isLoading={updateLoading}>
                    <form className={`${pageClassName}__modal`} onSubmit={submitHandler}>
                        <Input
                            label="Number of Additional License"
                            placeholder="Enter Additional License"
                            type="number"
                            value={payload.additional.value as number}
                            onChange={(e) =>
                                setPayload({
                                    ...payload,
                                    additional: {
                                        value: e.target.value,
                                        error: "",
                                        isError: false,
                                    },
                                })
                            }
                            error={payload.additional.error}
                            isError={payload.additional.isError}
                        />

                        <div className={`${pageClassName}__modal__suggest`}>
                            {[5, 10, 15, 20].map((item) => (
                                <button
                                    type="button"
                                    className={`${pageClassName}__modal__suggest__item`}
                                    key={item}
                                    onClick={() =>
                                        setPayload({
                                            ...payload,
                                            additional: {
                                                value: item,
                                                error: "",
                                                isError: false,
                                            },
                                        })
                                    }
                                >
                                    {item}
                                </button>
                            ))}
                        </div>

                        <Input
                            label="Number of Buffer License"
                            placeholder="Enter Buffer License"
                            type="number"
                            value={payload.buffer.value as number}
                            onChange={(e) =>
                                setPayload({
                                    ...payload,
                                    buffer: {
                                        value: e.target.value,
                                        error: "",
                                        isError: false,
                                    },
                                })
                            }
                            error={payload.buffer.error}
                            isError={payload.buffer.isError}
                        />

                        <div className={`${pageClassName}__modal__suggest`}>
                            {[5, 10, 15, 20].map((item) => (
                                <button
                                    type="button"
                                    className={`${pageClassName}__modal__suggest__item`}
                                    key={item}
                                    onClick={() =>
                                        setPayload({
                                            ...payload,
                                            buffer: {
                                                value: item,
                                                error: "",
                                                isError: false,
                                            },
                                        })
                                    }
                                >
                                    {item}
                                </button>
                            ))}
                        </div>
                        <Divider />
                        <div className={`${pageClassName}__modal__action`}>
                            <button
                                type="button"
                                onClick={() => setIsModalOpen(false)}
                                className={`${pageClassName}__modal__cancel`}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className={`${pageClassName}__modal__update`}
                            >Update</button>
                        </div>
                    </form>
                </Loader>
            </Modal>
        </div>
    );
};

export default LicenseRequisition;